import React, { useEffect, useState } from "react";
import { X, Check, Trash2, TrendingUp, TrendingDown, Plus, Loader2 } from "lucide-react";
import { CATEGORIES, INCOME_CATEGORIES, CATEGORY_COLORS, formatBRL } from "../lib/constants";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Confirmation sheet shown after voice/text NLP parse.
 * Lets user review/edit each detected transaction before saving to database.
 */
export default function ConfirmVoiceSheet({ open, onClose, payload, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const { api } = useAuth();

  useEffect(() => {
    if (!open) return;
    const src = (payload?.items || []).map((it, i) => ({
      key: `${i}-${Math.random().toString(36).slice(2, 7)}`,
      type: it.type,
      amount: it.amount,
      description: it.description,
      category: it.category,
    }));
    setItems(src);
  }, [open, payload]);

  if (!open) return null;

  const updateItem = (key, patch) => setItems((arr) => arr.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((arr) => arr.filter((it) => it.key !== key));
  const addBlank = () => setItems((arr) => [...arr, { key: `n-${Math.random().toString(36).slice(2, 7)}`, type: "expense", amount: 0, description: "", category: "Outros" }]);

  const confirm = async () => {
    const valid = items.filter((it) => it.amount > 0 && it.description.trim());
    if (valid.length === 0) { toast.error("Nenhum item válido para salvar"); return; }
    setSaving(true);
    try {
      const payload = valid.map((it) => ({
        type: it.type,
        amount: Number(it.amount),
        description: it.description.trim(),
        category: it.category,
        date: new Date().toISOString(),
        notes: "",
      }));
      const r = await api.post("/transactions/bulk", payload);
      onSaved(r.data);
      toast.success(`${r.data.length} movimentação(ões) salva(s)`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const total = items.reduce((s, it) => s + (it.type === "income" ? Number(it.amount) : -Number(it.amount)), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md" onClick={onClose}>
      <div
        data-testid="confirm-voice-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto animate-slide-up shadow-2xl"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Confira e confirme</p>
            <h2 className="font-display text-xl font-bold text-white">Confira antes de salvar</h2>
          </div>
          <button data-testid="confirm-close-btn" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        {payload?.transcript && (
          <p data-testid="confirm-transcript" className="text-xs text-slate-400 italic mb-4">
            Você disse: <span className="text-emerald-300 not-italic">“{payload.transcript}”</span>
          </p>
        )}

        <div className="space-y-3 mb-5">
          {items.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-6">Nenhum item para revisar.</p>
          )}
          {items.map((it) => (
            <div key={it.key} data-testid={`review-item-${it.key}`} className="rounded-2xl border border-white/10 bg-slate-800/50 p-3 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 p-0.5 rounded-full bg-slate-900/60 border border-white/5">
                  <button
                    data-testid={`item-type-expense-${it.key}`}
                    onClick={() => updateItem(it.key, { type: "expense", category: CATEGORIES.includes(it.category) ? it.category : "Outros" })}
                    className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${it.type === "expense" ? "bg-rose-500 text-white" : "text-slate-400"}`}
                  ><TrendingDown className="w-3 h-3" /> Saída</button>
                  <button
                    data-testid={`item-type-income-${it.key}`}
                    onClick={() => updateItem(it.key, { type: "income", category: INCOME_CATEGORIES.includes(it.category) ? it.category : "Trabalho" })}
                    className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${it.type === "income" ? "bg-emerald-500 text-white" : "text-slate-400"}`}
                  ><TrendingUp className="w-3 h-3" /> Entrada</button>
                </div>
                <button
                  data-testid={`item-remove-${it.key}`}
                  onClick={() => removeItem(it.key)}
                  className="text-rose-400 hover:text-rose-300 p-1.5 rounded-lg hover:bg-rose-500/10"
                ><Trash2 className="w-4 h-4" /></button>
              </div>

              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">Descrição</label>
                  <input
                    data-testid={`item-desc-${it.key}`}
                    value={it.description}
                    onChange={(e) => updateItem(it.key, { description: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900/70 border border-white/10 text-sm text-white focus:outline-none focus:border-indigo-400"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">Valor R$</label>
                  <input
                    data-testid={`item-amount-${it.key}`}
                    inputMode="decimal"
                    value={it.amount}
                    onChange={(e) => updateItem(it.key, { amount: e.target.value.replace(",", ".") })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900/70 border border-white/10 text-sm text-white font-mono-nums font-bold focus:outline-none focus:border-indigo-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">Categoria</label>
                <div className="mt-1.5 flex gap-1.5 overflow-x-auto pnc-scroll-hide">
                  {(it.type === "income" ? INCOME_CATEGORIES : CATEGORIES).map((c) => (
                    <button
                      key={c}
                      data-testid={`item-cat-${it.key}-${c}`}
                      onClick={() => updateItem(it.key, { category: c })}
                      className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border"
                      style={{
                        backgroundColor: it.category === c ? CATEGORY_COLORS[c] + "40" : "rgba(15,23,42,0.6)",
                        borderColor: it.category === c ? CATEGORY_COLORS[c] : "rgba(255,255,255,0.06)",
                        color: it.category === c ? "#fff" : "#cbd5e1",
                      }}
                    >{c}</button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          data-testid="add-blank-item-btn"
          onClick={addBlank}
          className="w-full mb-4 py-2 rounded-xl border border-dashed border-white/15 text-slate-300 hover:text-white hover:border-indigo-400 text-sm flex items-center justify-center gap-1.5"
        ><Plus className="w-4 h-4" /> Adicionar outra</button>

        <div className="flex items-center justify-between text-sm mb-3 px-1">
          <span className="text-slate-400">Total líquido</span>
          <span className={`font-mono-nums font-bold ${total >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {total >= 0 ? "+" : "−"} {formatBRL(Math.abs(total))}
          </span>
        </div>

        <div className="flex gap-2">
          <button
            data-testid="confirm-cancel-btn"
            onClick={onClose}
            className="px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold"
          >Cancelar</button>
          <button
            data-testid="confirm-save-btn"
            onClick={confirm}
            disabled={saving || items.length === 0}
            className="btn-emerald flex-1 py-3 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "Salvando…" : `Confirmar ${items.length > 0 ? `(${items.length})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
